import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { TextField } from "@mui/material";
import p4lLogo from "../assets/logo/PfL Logo no words 2023-11-21-01.png";
import signInClient from "../auth";
import Button from "../componentLibrary/Button";

type Step = "email" | "verification" | "success";

const ForgotUsernamePage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [usernames, setUsernames] = useState<string[]>([]);
  const [step, setStep] = useState<Step>("email");

  const navigate = useNavigate();

  const handleSendVerificationCode = async () => {
    setIsLoading(true);
    setError("");

    if (email.trim() === "") {
      setError("Please provide a valid email address.");
      setIsLoading(false);
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please provide a valid email address.");
      setIsLoading(false);
      return;
    }

    try {
      const validationResponse = await signInClient.post(
        "/forgot-username/validate-email",
        { email: email.toLowerCase() },
      );

      if (!validationResponse.data.exists) {
        setError("email-not-found");
        setIsLoading(false);
        return;
      }

      await signInClient.post("/forgot-username", {
        email: email.toLowerCase(),
      });
      setStep("verification");
      setIsLoading(false);
    } catch (error) {
      setError("Error sending verification code. Please try again.");
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    setIsLoading(true);
    setError("");

    if (verificationCode.trim() === "") {
      setError("Please enter the verification code.");
      setIsLoading(false);
      return;
    }

    if (verificationCode.length !== 6) {
      setError("Verification code must be 6 digits.");
      setIsLoading(false);
      return;
    }

    try {
      const response = await signInClient.post(
        "/forgot-username/verify-email",
        {
          email: email.toLowerCase(),
          verificationCode,
        },
      );

      setUsernames(response.data.usernames || []);
      setStep("success");
      setIsLoading(false);
    } catch (error: unknown) {
      let errorMessage = "Invalid verification code. Please try again.";

      if (axios.isAxiosError(error)) {
        errorMessage = error.response?.data?.message || errorMessage;
      }

      setError(errorMessage);
      setIsLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setStep("email");
    setVerificationCode("");
    setError("");
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  const renderContent = () => {
    if (step === "success") {
      return (
        <div className="space-y-4">
          <p className="text-gray-900 text-2xl font-bold">
            {usernames.length === 1
              ? "Username Retrieved"
              : "Usernames Retrieved"}
          </p>
          <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-4">
            <p className="text-sm font-medium text-green-900 mb-2">
              {usernames.length === 1 ? (
                <>
                  Your username is:{" "}
                  <span className="font-bold">{usernames[0]}</span>
                </>
              ) : (
                <>
                  Your usernames are:
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    {usernames.map((username, index) => (
                      <li key={index} className="font-bold">
                        {username}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </p>
          </div>
          <Button className="w-full" onClick={handleBackToLogin}>
            Back to Login
          </Button>
        </div>
      );
    }

    if (step === "verification") {
      return (
        <div className="space-y-4">
          <p className="text-gray-900 text-2xl font-bold">Verify Your Email</p>
          <p className="text-gray-600">
            Enter the 6-digit verification code that was sent to your email.
          </p>
          <TextField
            label="Email"
            variant="outlined"
            fullWidth
            type="email"
            name="email"
            value={email}
            disabled
            sx={{
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "#6b7280",
              },
            }}
          />
          <TextField
            label="Verification Code"
            variant="outlined"
            fullWidth
            name="verificationCode"
            value={verificationCode}
            onChange={(e) => {
              setError("");
              setVerificationCode(
                e.target.value.replace(/\D/g, "").slice(0, 6),
              );
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleVerifyCode();
              }
            }}
            inputProps={{
              maxLength: 6,
              pattern: "[0-9]*",
            }}
          />
          <Button
            className="w-full"
            onClick={handleVerifyCode}
            loading={isLoading}
          >
            Verify Code
          </Button>
          {error && <p className="text-red-500">{error}</p>}
          <div className="text-center">
            <button
              onClick={handleBackToEmail}
              className="text-sm text-blue-500 hover:text-blue-600"
            >
              Use a different email address
            </button>
          </div>
        </div>
      );
    }

    // Default: email step
    return (
      <div className="space-y-4">
        <p className="text-gray-900 text-2xl font-bold mb-4">Forgot Username</p>
        <p className="text-gray-600 mb-4">
          Enter your email address and we&apos;ll send you a verification code
          to retrieve your username(s).
        </p>
        <TextField
          label="Email"
          variant="outlined"
          fullWidth
          type="email"
          name="email"
          value={email}
          onChange={(e) => {
            setError("");
            setEmail(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleSendVerificationCode();
            }
          }}
        />
        <Button
          className="w-full"
          onClick={handleSendVerificationCode}
          loading={isLoading}
        >
          Send Verification Code
        </Button>
        {error === "email-not-found" ? (
          <p className="text-red-500">
            This email does not connect to an account with Psych for Life®.
            Please check your email address and try again.
          </p>
        ) : error ? (
          <p className="text-red-500">{error}</p>
        ) : null}
      </div>
    );
  };

  return (
    <div className="min-h-screen relative pb-6 bg-gradient-to-b from-electrum-100 via-white to-white">
      <div className="flex flex-col mx-auto w-full max-w-96 px-4 mt-24 mb-12 space-y-4">
        <div className="bg-white/90 backdrop-blur-sm rounded-lg shadow-lg p-8">
          <div className="flex flex-col items-center mb-8">
            <img
              src={p4lLogo}
              alt="Psych For Life logo"
              className="w-32 h-32 mb-2"
            />
            <p className="text-gray-900 text-3xl">
              Psych For Life<sup>®</sup>
            </p>
          </div>
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default ForgotUsernamePage;
